import { Channel } from "pusher-js";
import { useEffect, useRef } from "react";

import { usePusherChannelManager } from "./use-pusher-channel-manager";

const DEFAULT_THROTTLE_MS = 2000;

interface UsePusherMultiEventProps<T> {
  /** Array of channel IDs to subscribe to */
  channelIds: string[];

  /** The event type to listen for on all channels */
  eventType: string;

  /** Callback function called when an event is received from any channel */
  onEvent: (data: T, channelId: string) => void;

  /** Optional throttle time in ms (default: 2000ms) */
  throttleMs?: number;

  /** Optional flag to enable/disable all subscriptions */
  enabled?: boolean;
}

/**
 * A hook for subscribing to the same event type on multiple Pusher channels.
 * Useful for listening to updates from multiple conversations simultaneously.
 */
export function usePusherMultiEvent<T>({
  channelIds,
  eventType,
  onEvent,
  throttleMs = DEFAULT_THROTTLE_MS,
  enabled = true,
}: UsePusherMultiEventProps<T>): void {
  const { subscribe, unsubscribe } = usePusherChannelManager();
  const lastEventTimeRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestDataRef = useRef<{ data: T; channelId: string } | null>(null);
  const isMountedRef = useRef(true);

  // Store the callback in a ref to avoid effect re-runs
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  // Create a stable key for the channel list to use in dependency array
  const channelIdsKey = channelIds.slice().sort().join(",");

  useEffect(() => {
    isMountedRef.current = true;

    if (!enabled || channelIds.length === 0) {
      return undefined;
    }

    const activeChannels = new Map<string, { channel: Channel; callback: (data: T) => void }>();

    // Throttled callback factory - creates a callback for each channel
    const createThrottledCallback = (channelId: string) => {
      return (data: T) => {
        if (!isMountedRef.current) {
          return;
        }

        const now = Date.now();
        latestDataRef.current = { data, channelId };

        if (now - lastEventTimeRef.current >= throttleMs) {
          lastEventTimeRef.current = now;
          onEventRef.current(data, channelId);
        } else if (!timeoutRef.current) {
          const remainingTime = throttleMs - (now - lastEventTimeRef.current);
          timeoutRef.current = setTimeout(() => {
            if (!isMountedRef.current) {
              return;
            }
            lastEventTimeRef.current = Date.now();
            if (latestDataRef.current !== null) {
              onEventRef.current(latestDataRef.current.data, latestDataRef.current.channelId);
            }
            timeoutRef.current = null;
          }, remainingTime);
        }
      };
    };

    // Subscribe to all channels
    for (const channelId of channelIds) {
      try {
        const channel = subscribe(channelId);
        if (channel) {
          const callback = createThrottledCallback(channelId);
          channel.bind(eventType, callback);
          activeChannels.set(channelId, { channel, callback });
        }
      } catch {
        // Failed to subscribe to channel
      }
    }

    // Cleanup
    return () => {
      isMountedRef.current = false;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      activeChannels.forEach(({ channel, callback }, channelId) => {
        try {
          channel.unbind(eventType, callback);
          unsubscribe(channelId);
        } catch {
          // Cleanup error
        }
      });
      activeChannels.clear();
    };
  }, [subscribe, unsubscribe, channelIdsKey, eventType, throttleMs, enabled]);
}
